// src/styles/theme.js
// ---------------------------------------------------------------------------
// Pharvlink's single design language.
//
// These tokens are lifted from the admin Dashboard, which already reads as a
// finished product: light surfaces, one confident green, soft pastel icon
// tiles, generous radii. Every other page adopts THIS so the app stops looking
// like three different products stitched together.
//
// Rule: no page defines its own colours. Import from here. If a value is
// missing, add it here rather than inlining a hex somewhere.
// ---------------------------------------------------------------------------

export const C = {
  // surfaces
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceSunk: '#F1F5F9',

  // brand
  primary: '#16A34A',
  primaryDeep: '#15803D',
  primaryLight: '#DCFCE7',

  // accents (each has a soft tile colour for icon chips)
  blue: '#2563EB', blueLight: '#DBEAFE',
  purple: '#8B5CF6', purpleLight: '#F3E8FF',
  amber: '#F59E0B', amberLight: '#FEF3C7',
  red: '#EF4444', redLight: '#FEE2E2',
  indigo: '#6366F1', indigoLight: '#E0E7FF',
  cyan: '#06B6D4', cyanLight: '#CFFAFE',
  emerald: '#22C55E', emeraldLight: '#DCFCE7',

  // ink
  ink: '#0F172A',
  inkSoft: '#64748B',
  inkFaint: '#94A3B8',

  // structure
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
};

// Foreground tones for text sitting on a light tint.
//
// The plain accent colours in C are FILL colours. As text on their own *Light
// tile they run roughly 2.1-3.3:1, under the 4.5:1 readability floor - a blue
// status label on a blue chip is legible to some eyes and not to others, and
// money figures are the worst place to find that out. These are the 700-weight
// versions, which clear the floor on both white and the matching tint.
//
// Use C.blue to FILL something, FG.blue to write ON it.
export const FG = {
  primary: C.primaryDeep,   // #15803D
  blue: '#1D4ED8',
  purple: '#6D28D9',
  amber: '#92400E',
  red: '#B91C1C',
  indigo: '#4338CA',
  cyan: '#0E7490',
  emerald: '#15803D',
};

// Bookkeeping semantics, mapped onto the brand palette so the money pages
// speak the same colour language as everything else.
export const MONEY = {
  credit: C.primary,      // money arriving / owed to someone
  creditLight: C.primaryLight,
  debit: C.amber,         // money leaving / claimed back
  debitLight: C.amberLight,
  danger: C.red,
  dangerLight: C.redLight,
};

export const RADIUS = { sm: '8px', md: '12px', lg: '16px', xl: '20px', pill: '999px' };

export const SHADOW = {
  card: '0 1px 3px rgba(0,0,0,0.03)',
  lift: '0 8px 30px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)',
  pop: '0 8px 30px rgba(0,0,0,0.08)',
};

// Figures use tabular numerals so naira columns align to the kobo — that
// alignment is what lets you scan a column for something wrong.
export const figure = (size = 14, weight = 700) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: size + 'px',
  fontWeight: weight,
  letterSpacing: '-0.01em',
});

export const mono = {
  fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  fontVariantNumeric: 'tabular-nums',
};

export const card = {
  background: C.surface,
  borderRadius: RADIUS.xl,
  border: `1px solid ${C.border}`,
  boxShadow: SHADOW.card,
};

export const sectionTitle = {
  margin: 0,
  fontSize: '16px',
  fontWeight: 700,
  color: C.ink,
  letterSpacing: '-0.01em',
};

export const sectionNote = {
  margin: '2px 0 0',
  fontSize: '12.5px',
  color: C.inkSoft,
};

// Small uppercase label used above a group of cards or a column.
export const label = {
  margin: 0,
  fontSize: '11px',
  fontWeight: 700,
  color: C.inkFaint,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

export const button = (kind = 'quiet', disabled = false) => {
  const base = {
    padding: '9px 16px',
    borderRadius: RADIUS.sm,
    fontSize: '13px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    whiteSpace: 'nowrap',
  };
  const kinds = {
    primary: { background: C.primary, border: `1px solid ${C.primary}`, color: '#fff' },
    quiet:   { background: C.surface, border: `1px solid ${C.border}`, color: C.ink },
    subtle:  { background: C.surfaceSunk, border: `1px solid ${C.border}`, color: C.inkSoft },
    danger:  { background: C.redLight, border: `1px solid ${C.red}`, color: C.red },
    success: { background: C.primaryLight, border: `1px solid ${C.primary}`, color: C.primaryDeep },
    warn:    { background: C.amberLight, border: `1px solid ${C.amber}`, color: '#92400E' },
  };
  return { ...base, ...(kinds[kind] || kinds.quiet) };
};

export const field = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: RADIUS.sm,
  color: C.ink,
  padding: '9px 12px',
  fontSize: '13px',
  outline: 'none',
};

// Status pill — same shape everywhere, colour carries the meaning.
export const pill = (tone = 'neutral') => {
  const tones = {
    neutral: { bg: C.surfaceSunk, fg: C.inkSoft },
    green:   { bg: C.primaryLight, fg: FG.primary },
    blue:    { bg: C.blueLight, fg: FG.blue },
    amber:   { bg: C.amberLight, fg: FG.amber },
    red:     { bg: C.redLight, fg: FG.red },
    purple:  { bg: C.purpleLight, fg: FG.purple },
  };
  const t = tones[tone] || tones.neutral;
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: RADIUS.pill,
    background: t.bg,
    color: t.fg,
    fontSize: '11.5px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
};

// Pastel icon tile — the motif that makes the dashboard cards feel finished.
export const iconTile = (tint = C.primary, tintLight = C.primaryLight, size = 44) => ({
  width: size + 'px',
  height: size + 'px',
  borderRadius: RADIUS.md,
  background: tintLight,
  color: tint,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: Math.round(size * 0.45) + 'px',
  flexShrink: 0,
});

/* ---------------------------------------------------------------- money ---- */

export const naira = (kobo) => {
  const n = Number(kobo || 0) / 100;
  const sign = n < 0 ? '-' : '';
  return sign + '₦' + Math.abs(n).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
};

// For values already in naira (order columns), not kobo.
export const nairaPlain = (v) => '₦' + Number(v || 0).toLocaleString('en-NG');

export const shortId = (v) => (v ? String(v).slice(0, 8) : '—');

/* --------------------------- human references ---------------------------

   ONE PLACE, because there were four.

   AdminOrders, CustomerOrders and Payments took the LAST SIX characters of the
   UUID; ApprovalRequests took the FIRST EIGHT; theme.js took the first eight
   lowercase. So one order was #D73D97 to a customer and 4230AFBB on the
   approvals page, and nothing could be matched without opening the database.
   AdminQuotes went further and labelled a QUOTE id as "order #…" in vendor
   notifications.

   orders.order_number and quote_requests.quote_number already existed, unique
   and sequential, and were simply never displayed. These read from them.

   No zero padding: a sequence does not stop at 9999, and PV-0042 sets an
   expectation of fixed width that PV-10042 then breaks.
------------------------------------------------------------------------- */

// An order, as the customer, the vendor and the admin all see it: PV-42.
export const orderRef = (order) => {
  const n = typeof order === 'object' ? order?.order_number : order;
  return n === null || n === undefined || n === '' ? '—' : `PV-${n}`;
};

// A quote, before it becomes an order: Q-43. The two sequences run
// independently, so a quote number and its order number rarely match — which is
// why the reference changes at order creation rather than pretending they are
// the same thing.
export const quoteRef = (quote) => {
  const n = typeof quote === 'object' ? quote?.quote_number : quote;
  return n === null || n === undefined || n === '' ? '—' : `Q-${n}`;
};

// A vendor payout: PVP-3. Lives on the REQUEST, not on vendor_payouts, because
// a part-paid request produces two payout rows and the vendor thinks of that
// as one payout that arrived in two parts. The number therefore follows the
// whole life of it - submitted, promoted, approved, executed, receipted - so a
// vendor emailing about PVP-3 and an admin on the approvals page are naming
// the same thing.
export const payoutRef = (payout) => {
  const n = typeof payout === 'object' ? payout?.payout_number : payout;
  return n === null || n === undefined || n === '' ? '—' : `PVP-${n}`;
};

// An ACCOUNT, as it is quoted on a phone call: PVC-0007 for a pharmacy,
// PVV-0003 for a supplier.
//
// ZERO PADDED, unlike every reference above it, and deliberately so.
// assign_vendor_code established PVV-0001 before any of this existed, and one
// system reading two ways is worse than one inconsistency with a reason. The
// width bet is also safer here: a marketplace takes far more orders than it
// signs accounts, so PVC-10042 is a long way off where PV-10042 is not.
//
// Falls back to the short uuid only for a row that predates the codes or has
// somehow escaped the trigger — visibly different, so it reads as missing
// rather than passing itself off as a real code.
export const accountRef = (user) => {
  if (!user) return '—';
  return user.customer_code || user.vendor_code || shortId(user.id).toUpperCase();
};

// One vendor's portion: PV-42-3 once ordered, Q-43-3 while still quoting.
// A rescue creates a new vendor request and therefore takes the next number, so
// the suffix also says how many vendors the work has passed through.
export const vendorJobRef = (parentRef, seq) =>
  (seq === null || seq === undefined ? parentRef : `${parentRef}-${seq}`);

export const ACCOUNT_LABELS = {
  customer_escrow: 'Customer escrow',
  delivery_fee_held: 'Delivery fee held',
  delivery_fee_earned: 'Delivery fee earned',
  vendor_payable: 'Vendor payable',
  customer_wallet: 'Customer wallet',
  vendor_wallet: 'Vendor wallet',
  customer_refund_payable: 'Customer refund payable',
  customer_rewards: 'Customer rewards',
  agent_rewards_payable: 'Agent rewards payable',
  bank_float: 'Bank float',
  platform_commission: 'Platform commission',
  promotions_budget: 'Promotions budget',
  medvault_wallet: 'Pharvlink wallet',
  absorbed_losses: 'Absorbed losses',
  treasury_wallet: 'Treasury wallet',
  payout_wallet: 'Payout wallet',
  director_loan: 'Director loan',
  retained_earnings: 'Retained earnings',
  payment_processing_fees: 'Payment processing fees',
  stamp_duty_expense: 'Stamp duty',
};
export const accountLabel = (code) => ACCOUNT_LABELS[code] || code;

// Name what happened to the money, not the function that wrote the row.
export const OPERATION_LABELS = {
  order_payment: 'Order payment',
  vendor_earning: 'Vendor settled',
  platform_earning: 'Pharvlink settled',
  delivery_release: 'Delivery fee released',
  order_refund: 'Order refund',
  rescue_refund: 'Rescue refund',
  absorb_gap: 'Gap absorbed',
  vendor_payout: 'Vendor payout',
  refund_payout: 'Refund payout',
  referral_reward: 'Referral reward',
  cash_deposit: 'Cash deposit',
  cash_withdrawal: 'Cash withdrawal',
  treasury_transfer: 'Treasury transfer',
  reversal: 'Reversal',
  payment_credit: 'Transfer received',
  wallet_topup: 'Account credited',
  manual_credit: 'Manual credit',
};
export const operationLabel = (op) =>
  OPERATION_LABELS[op] || String(op || '').replace(/_/g, ' ');

// Order status -> pill tone, using the real CHECK values on orders.status.
export const ORDER_STATUS_TONE = {
  confirmed: 'blue',
  pending_payment: 'amber',
  in_process: 'blue',
  ready_for_pickup: 'purple',
  out_for_delivery: 'purple',
  delivered: 'green',
  cancelled: 'red',
};

export const PAYMENT_STATUS_TONE = {
  pending: 'amber',
  pending_verification: 'amber',
  paid: 'green',
  refunded: 'neutral',
};

export const prettyStatus = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/* ------------------------------------------------------------- grouping ---- */
// Groups ledger legs into their transactions. A transaction is the atomic unit
// of the ledger, not the leg, so the UI groups by it and can then show that it
// nets to zero.
//
// Keyed by Map, NOT by watching for consecutive rows: every leg of one
// transaction shares the same created_at, so the sort falls through to id and
// interleaves transactions posted in the same instant. Consecutive grouping
// turned one balanced 8-leg movement into eight "unbalanced" single rows.
export const groupByTransaction = (entries) => {
  const order = [];
  const byTxn = new Map();

  (entries || []).forEach((e) => {
    const key = e.transaction_id || e.id;
    if (!byTxn.has(key)) {
      byTxn.set(key, {
        txnId: key, legs: [], debits: [], credits: [],
        debitTotal: 0, creditTotal: 0, net: 0,
      });
      order.push(key);
    }
    const g = byTxn.get(key);
    const amt = Number(e.amount_kobo || 0);
    g.legs.push(e);
    if (e.direction === 'credit') { g.credits.push(e); g.creditTotal += amt; g.net += amt; }
    else { g.debits.push(e); g.debitTotal += amt; g.net -= amt; }
  });

  return order.map((k) => {
    const g = byTxn.get(k);
    g.balanced = g.debitTotal === g.creditTotal;
    g.at = g.legs[0]?.created_at || null;
    g.operation = g.legs[0]?.operation || null;
    g.orderId = g.legs.find((l) => l.order_id)?.order_id || null;
    g.note = g.legs[0]?.note || null;
    g.reversed = g.legs.some((l) => l.operation === 'reversal');
    return g;
  });
};